import { notFoundPage } from "../utils/notFound";
import axios from "axios";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { capitalizeFirstLetter, getDisplayName2 } from "../utils/tools";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/StaticAccordion";
import { toast } from "react-hot-toast";
import { getIdToken } from "../utils/firebaseAuth";
import { NextSeo } from "next-seo";
import { LOCALE_META, asLocale, useT } from "@jammable/ui-core/src/i18n";
import { categoryLabel, categoryName } from "../utils/categoryNames";
import { localePath, localeSeo, localeUrl } from "../utils/localeSeo";
import Link from "next/link";
import { useRouter } from "next/router";
import { auth } from "../utils/firebaseAdmin";
import millify from "millify";
import { useModal } from "../context/ModalContext";
import { useAuth } from "../context/AuthProvider";
import useAuthModal from "../hooks/useAuthModal";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import "dayjs/locale/pt-br";
import "dayjs/locale/fr";
import "dayjs/locale/de";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import "dayjs/locale/id";
import "dayjs/locale/tr";
import {
  ArrowRight,
  Ban,
  Flag,
  Heart,
  Lock,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Plus,
  Share2,
  Sparkles,
  Star,
  Trash2,
} from "lucide-react";
import { useQuery } from "react-query";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { Spinner } from "@jammable/ui-components/Loader";
import Dropdown from "../components/dropdown/Dropdown";
import VoiceImage from "@/components/VoiceImage";
import connectToDb from "@/utils/mongodb";
import dynamic from "next/dynamic";
import { MODEL_PROJECTION } from "./api/model";
import Image from "next/image";
import DiscordCta from "../components/DiscordCta";
import {
  JamVoiceCard,
  JamCardSkeleton,
  JamVoiceCardSkeleton,
} from "../components/home/JamCards";
// The demos, try-it and comments sections are imported statically. As
// next/dynamic components (ssr: true) they were server-rendered and their
// chunks preloaded on a full load anyway, so splitting saved nothing there;
// but on a client-side navigation (a voice card tapped on home, /models or a
// hub - the usual way in) each showed a fixed h-64 placeholder until its
// chunk arrived, then swapped in content of a different height. The page
// jumped (0.21 CLS in the 2026-10-10 probe), and CrUX books a soft
// navigation's shifts to the page it started on - home's CLS group.
import LazyTryVoiceSection, {
  sortedDemoVoices,
} from "../components/TryVoiceSection";
import LazyModelCommentSection from "../components/ModelCommentSection";
import LazyLibraryDemosSection from "@/components/LibraryDemosSection";

// Dynamically import all modals to reduce initial bundle size

const EditModelModal = dynamic(
  () => import("../components/modals/EditModelModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const DeleteModelModal = dynamic(
  () => import("../components/modals/DeleteModelModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const ReportModal = dynamic(() => import("../components/modals/ReportModal"), {
  ssr: false,
  loading: () => (
    <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
      <Spinner size={8} className="tw-border-3" />
    </div>
  ),
});
const AddToCollectionModal = dynamic(
  () => import("../components/collection/modals/AddToCollectionModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const CreateCollectionModal = dynamic(
  () => import("@/components/collection/modals/CreateCollectionModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const ModelCollectionsWithVoice = dynamic(
  () => import("@/components/ModelCollectionsWithVoice"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);

// Badge string -> /ai-voices/[slug] category hub (kept in sync with
// utils/categoryHubs slugifyBadge).
const badgeToSlug = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// FAQ rows: white cards on the lavender FAQ panel. The padding lives on the
// trigger and content, not the item: AccordionItem appends its own p-0.5
// after our className, which cancelled a px on the item.
const faqItem = "tw-overflow-hidden tw-rounded-[22px] tw-bg-white";
const faqTrigger =
  "tw-gap-4 tw-px-5 tw-py-[18px] tw-text-[15px] tw-font-extrabold tw-leading-snug tw-tracking-normal tw-text-jam-ink hover:tw-no-underline md:tw-px-6 [&>svg]:tw-text-jam-purple";
const faqContent =
  "tw-px-5 tw-pb-5 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted md:tw-px-6";

const ModelPage = ({ modelData, isPrivate, isRemoved, token }) => {
  // Initialize dayjs plugin once at module level
  if (!dayjs?.plugins?.relativeTime) {
    dayjs.extend(relativeTime);
  }

  const { user, loading: authLoading } = useAuth();
  const { checkAuthAndExecute } = useAuthModal();

  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const [isLiked, setIsLiked] = useState(modelData?.isLiked);
  const [likes, setLikes] = useState(modelData?.likes ? modelData?.likes : 0);

  const { openModal, closeModal } = useModal();
  const router = useRouter();
  const t = useT();
  const pageLocale = asLocale(router.locale);
  // dayjs's locale is set per call: changing the global one would leak
  // between requests on the server.
  const dayjsLocale = LOCALE_META[pageLocale].dayjs;
  const numberLocale = LOCALE_META[pageLocale].intl;
  // Badges are English free text; translated where utils/categoryNames.ts
  // knows them. English output is unchanged.
  const badgeLabel = (b) =>
    pageLocale === "en"
      ? capitalizeFirstLetter(b.trim())
      : categoryLabel(pageLocale, b);
  const badgeInSentence = (b) =>
    pageLocale === "en"
      ? b.trim().toLowerCase()
      : categoryName(pageLocale, b);

  // A private voice is a 404 unless the request's token cookie belongs to its
  // owner, and that cookie is an ID token that expires after an hour. An owner
  // coming back later is served the 404 first; once auth has refreshed the
  // cookie, reload once so getServerSideProps can see who they are. A full
  // reload, not router.replace: the client router treats a 404 data response
  // as an error. At most once per voice per 5 minutes, so a signed-in
  // non-owner is reloaded once and never loops; without sessionStorage there
  // is no guard, so no reload either.
  useEffect(() => {
    if (!isPrivate || authLoading || !user) return;
    try {
      const key = `private-voice-recheck:${router.asPath}`;
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < 5 * 60 * 1000) return;
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    } catch {
      // Storage blocked: stay on the panel rather than risk a reload loop.
    }
  }, [isPrivate, authLoading, user, router.asPath]);

  // Generated, quality-gated page content (seoContent) overrides the shared
  // template where present — query-matched title/meta, unique intro, HowTo,
  // and per-voice FAQ. Pages without it keep the template. seoContent is
  // English text, so /es and /pt use the translated template instead; only
  // its related-voice links (names, not prose) are shown in every language.
  const seo = pageLocale === "en" ? modelData?.seoContent : undefined;
  const seoRelated = modelData?.seoContent?.related;
  const name = modelData?.artistname;
  // Default title/meta lead with the entertainment hook ("hear it") — most
  // impressions on these pages come from bare character-name searches where
  // "Generator & Voice Changer" reads like a tool ad and earns ~0% CTR.
  const title = seo?.metaTitle || t("voice.seo.title", { name });
  const description =
    seo?.metaDescription || t("voice.seo.description", { name });
  const url = `https://www.jammable.com/${modelData?.urlParam}`;
  // Duplicate-voice consolidation: when this model is a vetted duplicate of a
  // stronger page for the same character, point rel=canonical at the winner
  // so search signals concentrate on one page. Page stays fully usable.
  const canonicalUrl = modelData?.canonicalUrlParam
    ? `https://www.jammable.com/${modelData.canonicalUrlParam}`
    : url;

  // Thin voices (under 5 uses after 30 days, or an auto-generated name) are
  // noindex,follow unless they already earn clicks. Decided on the server by
  // utils/voiceIndexing.ts, the same rule the sitemaps and hubs use.
  const noindexThinPage = modelData?.indexable === false;
  const localized = localeSeo(pageLocale, canonicalUrl, {
    locales: modelData?.indexLocales ?? ["en"],
    noindex: noindexThinPage,
  });

  const likeModel = async () => {
    if (!checkAuthAndExecute()) return false;
    if (isLiked) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    setIsLiked(!isLiked);
    const token = await getIdToken();
    if (!token) {
      return;
    }
    axios
      .post(
        `/api/user/likeModel`,
        { modelId: modelData?._id },
        { headers: { Authorization: token } },
      )
      .then((res) => {
        toast.success(res?.data.message);
      })
      .catch((err) => {
        toast.success(t("voice.error"));
        console.error("Error liking model:", err);
      });
  };

  // The page's Create button (in the hero) goes to the studio with this voice
  // preselected. Creating does not happen inline on the page, and sign-in is
  // asked for at Create inside the studio, not before it opens.
  const openCreateFlow = () => {
    router.push(
      `/create?voice=${encodeURIComponent(modelData?.urlParam || modelData?._id)}`,
    );
  };

  const openAddToCollection = () => {
    if (!checkAuthAndExecute()) return false;
    openModal(
      "add-to-collection-modal",
      <AddToCollectionModal
        model={modelData}
        reroute={true}
        closeModal={() => closeModal("add-to-collection-modal")}
        openCreate={() => {
          closeModal("add-to-collection-modal");

          setTimeout(() => {
            openModal("create-collection-modal", <CreateCollectionModal />);
          }, 100);
        }}
      />,
    );
  };

  const badges = [...(modelData?.badges ?? [])].slice(0, 3);
  // Only badges that have a live /ai-voices/[slug] hub get a link. On
  // 2026-10-09, 2,213 of the 2,553 badge slugs shown on submitted voice pages
  // returned 404 - "other" alone, on 11,094 pages. hubBadgeSlugs comes from
  // getServerSideProps; when it is missing (old cached props) link nothing.
  const hubBadgeSlugs = new Set(modelData?.hubBadgeSlugs ?? []);
  const hasHub = (badge) => hubBadgeSlugs.has(badgeToSlug(badge));
  // The badge related voices come from (the most specific one, see
  // getServerSideProps). It may have too few voices for a hub page, so the
  // breadcrumb and "See all" use it only when it has one.
  const relatedBadge = badges.find(
    (b) => badgeToSlug(b) === modelData?.relatedBadgeSlug,
  );
  const hubBadge =
    relatedBadge && hasHub(relatedBadge) ? relatedBadge : badges.find(hasHub);
  // Demos that exist, in the order the section shows them (AudioObject
  // schema; the section itself renders the <audio> elements).
  const demoAudio = sortedDemoVoices(modelData?.tts?.gender)
    .map((v) => {
      const d = modelData?.demos?.find((x) => x?.name === v.param);
      return d?.convertedUrl && d?.status !== "failed"
        ? { url: d.convertedUrl, label: v.name }
        : null;
    })
    .filter(Boolean);

  const similarVoiceData = useQuery(
    ["similar-voices", modelData?._id],
    async () => {
      const { data } = await axios.get(`/api/model/getDuetMatches`, {
        params: {
          modelIds: modelData?._id,
          limit: 6,
        },
      });
      return data;
    },
    {
      enabled: !!modelData?._id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const collections = useQuery(
    ["collections", modelData?._id],
    async () => {
      const { data } = await axios.get(`/api/model/getCollections`, {
        params: {
          modelId: modelData?._id,
        },
      });
      return data;
    },
    {
      enabled: !!modelData?._id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const commentSectionRef = useRef(null);
  const scrollToComments = () => {
    if (commentSectionRef.current) {
      const yOffset = -130;
      const element = commentSectionRef.current;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Memoize the demo count calculation
  const librarySongDemoSuccessCount = useMemo(() => {
    return (
      modelData?.librarySongDemos?.filter((demo) => demo.status === "processed")
        ?.length || 0
    );
  }, [modelData?.librarySongDemos]);

  // Decided in getServerSideProps from the token cookie; owner uids
  // (accessIds) are not sent to the page.
  const isOwner = !!modelData?.isOwner;

  const pillAction =
    "tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-4 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line";
  const linkPill =
    "tw-rounded-full tw-bg-white tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple";
  const menuItem =
    "tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-p-2 tw-pl-3 tw-font-extrabold tw-text-jam-ink hover:tw-bg-jam-lav";
  const menuItemDanger =
    "tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-bg-red-50 tw-p-2 tw-pl-3 tw-font-extrabold tw-text-red-600 hover:tw-bg-red-100";
  const h2 =
    "jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-jam-ink md:tw-text-[28px]";

  // Text around an inline element: the translation holds {name} / {dmca}
  // where the element goes.
  const uploadedBy = t("voice.uploadedBy").split("{name}");
  const howAnswer = t("voice.faq.how.a", { name }).split("{dmca}");

  // Only the blocked panels use this, and they get no voice data (a private
  // voice is served as a 404 with empty props), so there is nothing to name.
  const seoTags = <NextSeo title={t("voice.seo.private")} noindex />;

  // Removed / private voices: one centred lavender panel, same exits as before.
  const blockedState = (icon, heading, actions) => (
    <div className="tw-flex tw-min-h-[70vh] tw-items-center tw-justify-center tw-bg-white tw-px-4">
      {seoTags}
      <div className="tw-flex tw-w-full tw-max-w-[520px] tw-flex-col tw-items-center tw-gap-4 tw-rounded-[36px] tw-bg-jam-lav tw-px-6 tw-py-12 tw-text-center tw-text-jam-ink">
        <span className="tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-jam-purple">
          {icon}
        </span>
        <div className="jam-display tw-text-[30px] tw-font-extrabold tw-leading-tight">
          {heading}
        </div>
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
          {actions}
        </div>
      </div>
    </div>
  );

  return isRemoved ? (
    blockedState(
      <Ban size={36} strokeWidth={2.4} />,
      t("voice.removed"),
      <Link
        href={`/models`}
        className="tw-rounded-full tw-bg-jam-purple tw-px-6 tw-py-3 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline"
      >
        {t("voice.browseOther")}
      </Link>,
    )
  ) : isPrivate ? (
    blockedState(
      <Lock size={34} strokeWidth={2.4} />,
      t("voice.private"),
      <>
        <Link
          href={`/models`}
          className="tw-rounded-full tw-bg-jam-purple tw-px-6 tw-py-3 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline"
        >
          {t("voice.browseOther")}
        </Link>
        <Link
          href={`/login`}
          className="tw-rounded-full tw-bg-white tw-px-6 tw-py-3 tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline"
        >
          {t("auth.signIn")}
        </Link>
      </>,
    )
  ) : (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        // One robots tag: NextSeo always prints its own, and a second one in
        // additionalMetaTags gave every voice page two that disagreed.
        noindex={localized.noindex}
        title={title}
        description={description}
        canonical={localized.canonical}
        languageAlternates={localized.languageAlternates}
        openGraph={{
          title: title,
          description: description,
          url: localeUrl(pageLocale, url),
          locale: localized.ogLocale,

          images: [
            {
              url: modelData?.image?.url,
              width: 900,
              height: 900,
              alt: t("voice.name", { name }),
            },
          ],
        }}
      />
      {/* The page and its demos. Was SoftwareApplication with a price of
          "0": Jammable is a paid product, and the voice page is about a
          voice, so WebPage + the demos as AudioObjects says what it is. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description: description,
            url: localeUrl(pageLocale, url),
            inLanguage: LOCALE_META[pageLocale]?.htmlLang,
            primaryImageOfPage: modelData?.image?.url,
            ...(demoAudio.length > 0 && {
              audio: demoAudio.map((d) => ({
                "@type": "AudioObject",
                name: `${name} - ${d.label}`,
                description: t("voice.seo.appName", { name }),
                contentUrl: d.url,
                encodingFormat: "audio/wav",
                inLanguage: "en",
                ...(modelData?.createdAt && {
                  uploadDate: new Date(modelData.createdAt)
                    .toISOString()
                    .slice(0, 10),
                }),
              })),
            }),
          }),
        }}
      />
      {seo?.faq?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: seo.faq.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
            }),
          }}
        />
      )}
      {seo?.howToSteps?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: t("voice.howTo.title", { name }),
              step: seo.howToSteps.map((s, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: s.name,
                text: s.text,
              })),
            }),
          }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("hub.home"),
                item: localeUrl(pageLocale, "https://www.jammable.com"),
              },
              // The voice's category hub when it has one (the trail people
              // can follow up), else the whole library.
              hubBadge
                ? {
                    "@type": "ListItem",
                    position: 2,
                    name: t("voice.popularHub.title", {
                      badge: badgeInSentence(hubBadge),
                    }),
                    item: localeUrl(
                      pageLocale,
                      `https://www.jammable.com/ai-voices/${badgeToSlug(hubBadge)}`,
                    ),
                  }
                : {
                    "@type": "ListItem",
                    position: 2,
                    name: t("nav.footer.voiceLibrary"),
                    item: localeUrl(
                      pageLocale,
                      "https://www.jammable.com/models",
                    ),
                  },
              {
                "@type": "ListItem",
                position: 3,
                name: t("voice.name", { name }),
                item: localeUrl(pageLocale, url),
              },
            ],
          }),
        }}
      />
      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-[22px] tw-px-4 tw-pb-12 tw-pt-2 md:tw-px-8">
        {modelData?.newerVersion?._id && (
          <Link
            href={`/${modelData?.newerVersion?.urlParam || modelData?.newerVersion?._id}`}
            className="tw-flex tw-items-center tw-gap-3 tw-rounded-[22px] tw-bg-[#FFF6CC] tw-px-4 tw-py-3 tw-text-jam-ink tw-no-underline"
          >
            <span className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#F7D84A]">
              <Sparkles size={16} strokeWidth={2.6} />
            </span>
            <span className="tw-flex-grow tw-text-sm tw-font-extrabold">
              {t("voice.newer.body", { username: modelData?.username })}
            </span>
            <span className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-text-[13px] tw-font-extrabold tw-text-[#7A6400]">
              <span className="tw-hidden sm:tw-inline">
                {t("voice.newer.cta")}
              </span>
              <ArrowRight size={16} strokeWidth={2.6} />
            </span>
          </Link>
        )}

        <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-8">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-8">
            {/* Hero: image, badges, name, creator, then the actions. */}
            <section className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center md:tw-flex-row md:tw-items-center md:tw-gap-6 md:tw-text-left">
              <div className="tw-relative tw-h-[132px] tw-w-[132px] tw-flex-shrink-0 tw-overflow-hidden tw-rounded-[38px] tw-bg-jam-lav md:tw-h-[168px] md:tw-w-[168px] md:tw-rounded-[44px]">
                <VoiceImage
                  model={modelData}
                  fill
                  sizes="168px"
                  quality={75}
                  className="tw-object-cover"
                  priority={true}
                  placeholder="blur"
                  blurDataURL={modelData?.image?.blurDataURL}
                />
              </div>
              <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-gap-3 md:tw-items-start">
                {(modelData?.isCommunityFavorite || badges.length > 0) && (
                  <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-1.5 md:tw-justify-start">
                    {modelData?.isCommunityFavorite && (
                      <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold">
                        <Star size={12} strokeWidth={3} />
                        {t("voice.communityFavourite")}
                      </span>
                    )}
                    {badges?.map((badge) =>
                      hasHub(badge) ? (
                        <Link
                          key={badge}
                          href={`/ai-voices/${badgeToSlug(badge)}`}
                          className="tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
                        >
                          {badgeLabel(badge)}
                        </Link>
                      ) : (
                        <span
                          key={badge}
                          className="tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink"
                        >
                          {badgeLabel(badge)}
                        </span>
                      ),
                    )}
                  </div>
                )}
                <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-[1.02] md:tw-text-[46px] xl:tw-text-[54px] xl:tw-leading-none">
                  {t("voice.name", { name: getDisplayName2(modelData) })}
                </h1>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-3.5 tw-gap-y-2 tw-text-sm tw-font-bold tw-text-jam-muted md:tw-justify-start">
                  {modelData?.hasCreator ? (
                    <Link
                      href={`/@${modelData?.username}`}
                      className="tw-flex tw-items-center tw-gap-2 tw-no-underline"
                    >
                      <Image
                        src={modelData?.avatar}
                        width={26}
                        height={26}
                        alt={modelData?.username}
                        className="tw-rounded-full tw-bg-jam-line"
                      />
                      <span className="tw-font-extrabold tw-text-jam-ink">
                        @{modelData?.username}
                      </span>
                    </Link>
                  ) : (
                    <span>
                      {uploadedBy[0]}
                      <span className="tw-font-extrabold tw-text-jam-ink">
                        {modelData?.username}
                      </span>
                      {uploadedBy[1]}
                    </span>
                  )}
                  {/* Rendered on the server too. Client-only, the date
                      arrived after hydration and, in this centred wrapping
                      row on phones, pushed the collection pill to a new line
                      and the whole hero below it down (CLS). Relative dates
                      are coarse ("2 years ago"), so server and client
                      agree; when they don't, React just patches the text. */}
                  <span suppressHydrationWarning>
                    {modelData?.createdAt &&
                      dayjs(modelData.createdAt).locale(dayjsLocale).fromNow()}
                  </span>
                  {modelData?.collection &&
                    modelData?.collection?.modelsCount > 2 && (
                      <Link
                        href={`/collection/${modelData?.collection?._id}`}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lav tw-py-1 tw-pl-1 tw-pr-2.5 tw-no-underline"
                      >
                        <span className="tw-relative tw-h-[22px] tw-w-[22px] tw-overflow-hidden tw-rounded-full tw-bg-jam-line">
                          {modelData?.collection?.image?.url && (
                            <VoiceImage
                              model={{ image: modelData?.collection?.image }}
                              fill
                              sizes="22px"
                              className="tw-object-cover"
                            />
                          )}
                        </span>
                        <span className="tw-font-extrabold tw-text-jam-ink">
                          {modelData?.collection?.title
                            ? `${modelData.collection.title} · `
                            : ""}
                          {t(
                            modelData?.collection?.modelsCount === 1
                              ? "voice.voices.one"
                              : "voice.voices.other",
                            { n: modelData?.collection?.modelsCount },
                          )}
                        </span>
                      </Link>
                    )}
                </div>
                <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 md:tw-justify-start">
                  <button
                    type="button"
                    onClick={openCreateFlow}
                    className="tw-flex tw-h-12 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-jam-purple tw-px-[22px] tw-text-[15px] tw-font-extrabold tw-text-white sm:tw-w-auto"
                  >
                    {t("voice.createCover", {
                      uses: millify(modelData?.uses || 0),
                    })}
                  </button>
                  <button
                    type="button"
                    onClick={likeModel}
                    aria-pressed={!!isLiked}
                    aria-label={isLiked ? t("voice.unlike") : t("voice.like")}
                    className={`${pillAction} ${isLiked ? "tw-text-jam-purple" : ""}`}
                  >
                    <Heart
                      size={16}
                      strokeWidth={2.5}
                      fill={isLiked ? "currentColor" : "none"}
                    />
                    {likes.toLocaleString(numberLocale)}
                  </button>
                  <button
                    type="button"
                    onClick={scrollToComments}
                    aria-label={t("voice.comments")}
                    className={pillAction}
                  >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    {(modelData?.comments ?? 0).toLocaleString(numberLocale)}
                  </button>
                  <button
                    type="button"
                    onClick={openAddToCollection}
                    aria-label={t("voice.saveToCollection")}
                    className={pillAction}
                  >
                    <Plus size={16} strokeWidth={2.6} />
                    <span className="tw-hidden sm:tw-inline">
                      {t("voice.saveToCollection")}
                    </span>
                  </button>

                  <Dropdown
                    dropdownItems={
                      <>
                        <DropdownMenuItem
                          onClick={async () => {
                            const textToCopy = `https://www.jammable.com/${modelData?.urlParam}`;
                            navigator.clipboard.writeText(textToCopy);
                            toast.success(t("voice.copied"));
                          }}
                          className={menuItem}
                        >
                          <Share2 size={16} strokeWidth={2.5} />
                          {t("voice.share")}
                        </DropdownMenuItem>
                        {isOwner && (
                          <DropdownMenuItem
                            onClick={() => {
                              openModal(
                                "edit-model-modal",
                                <EditModelModal
                                  model={modelData}
                                  reroute={true}
                                />,
                              );
                            }}
                            className={menuItem}
                          >
                            <Pencil size={16} strokeWidth={2.5} />
                            {t("voice.edit")}
                          </DropdownMenuItem>
                        )}

                        {isClient && !isOwner && (
                          <DropdownMenuItem
                            onClick={() => {
                              if (!checkAuthAndExecute()) return false;
                              openModal(
                                "report-modal",
                                <ReportModal
                                  item={modelData}
                                  type="voice"
                                  closeModal={() => closeModal("report-modal")}
                                />,
                              );
                            }}
                            className={menuItemDanger}
                          >
                            <Flag size={16} strokeWidth={2.5} />
                            <span>{t("voice.report")}</span>
                          </DropdownMenuItem>
                        )}
                        {isOwner && (
                          <DropdownMenuItem
                            onClick={() => {
                              openModal(
                                "delete-conversion-modal",
                                <DeleteModelModal
                                  model={modelData}
                                  closeModal={() =>
                                    closeModal("delete-model-modal")
                                  }
                                />,
                              );
                            }}
                            className={menuItemDanger}
                          >
                            <Trash2 size={16} strokeWidth={2.5} />
                            <span>{t("voice.delete")}</span>
                          </DropdownMenuItem>
                        )}
                      </>
                    }
                  >
                    <div
                      aria-label={t("voice.more")}
                      className="tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line"
                    >
                      <MoreHorizontal size={20} strokeWidth={2.6} />
                    </div>
                  </Dropdown>
                </div>
              </div>
            </section>

            {/* "weights ai" / "weights gg" searches land on the Weights voices
                (1,648 + 1,439 clicks in 90 days on the announcer voice),
                not on /weights-gg-alternative: point them there in words. */}
            {/weights/i.test(modelData?.urlParam || "") && (
              <p className="tw-m-0 tw-rounded-[22px] tw-bg-jam-lav tw-px-5 tw-py-4 tw-text-[15px] tw-font-semibold">
                {t("voice.weights.callout")}{" "}
                <Link
                  href="/weights-gg-alternative"
                  className="tw-font-extrabold tw-text-jam-purple"
                >
                  {t("voice.weights.calloutLink")}
                </Link>
              </p>
            )}
            {modelData?.description && (
              <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-px-5 tw-py-4">
                <div className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-jam-muted">
                  {t("voice.description")}
                </div>
                <div className="tw-text-pretty tw-text-[15px] tw-font-semibold">
                  {modelData?.description}
                </div>
              </div>
            )}

            {librarySongDemoSuccessCount > 1000 ? (
              <LazyLibraryDemosSection modelData={modelData} />
            ) : (
              <LazyTryVoiceSection modelData={modelData} />
            )}

            <div ref={commentSectionRef}>
              <LazyModelCommentSection
                user={user}
                model={modelData}
                token={token}
                initialComments={modelData?.initialComments}
              />
            </div>

            <DiscordCta
              variant="soft"
              label={
                name
                  ? t("voice.discord.label", { name })
                  : t("voice.discord.labelNoName")
              }
            >
              {t("voice.discord.body")}
            </DiscordCta>
          </div>
        </div>

        {similarVoiceData?.isLoading && !similarVoiceData?.isError ? (
          // Same footprint as the loaded section below (heading, then one
          // scrolling row on phones / six columns on desktop). The old
          // skeleton was a 3-column grid, two rows tall on phones, so the
          // page below jumped up ~170px when the voices arrived.
          <section
            className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5"
            aria-hidden
          >
            <div>
              <div className="tw-h-[27px] tw-w-2/3 tw-animate-pulse tw-rounded-full tw-bg-jam-lav md:tw-h-[34px]" />
              <div className="tw-mt-1 tw-h-[18px] tw-w-1/2 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
            </div>
            <div className="tw-flex tw-gap-2.5 tw-overflow-hidden tw-pb-1 md:tw-grid md:tw-grid-cols-6 md:tw-gap-3.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="tw-w-[132px] tw-flex-shrink-0 md:tw-w-auto"
                >
                  <JamVoiceCardSkeleton />
                </div>
              ))}
            </div>
          </section>
        ) : similarVoiceData?.data?.models?.length === 0 ||
          similarVoiceData?.isError ? null : (
          <section className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5">
            <div className="tw-flex tw-items-end tw-justify-between tw-gap-3">
              <div>
                <h2 className={h2}>{t("voice.similar.title", { name })}</h2>
                <div className="tw-mt-1 tw-text-[13px] tw-font-bold tw-text-jam-muted">
                  {t("voice.similar.sub", { name })}
                </div>
              </div>
              {hubBadge && (
                <Link
                  href={`/ai-voices/${badgeToSlug(hubBadge)}`}
                  className="tw-hidden tw-whitespace-nowrap tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline md:tw-block"
                >
                  {t("voice.similar.moreHub", {
                    badge: badgeInSentence(hubBadge),
                  })}
                </Link>
              )}
            </div>
            <div className="tw-flex tw-gap-2.5 tw-overflow-x-auto tw-pb-1 md:tw-grid md:tw-grid-cols-6 md:tw-gap-3.5 md:tw-overflow-visible [&::-webkit-scrollbar]:tw-hidden">
              {similarVoiceData?.data?.models?.map((model) => (
                <div
                  key={model._id}
                  className="tw-w-[132px] tw-flex-shrink-0 md:tw-w-auto"
                >
                  <JamVoiceCard model={model} />
                </div>
              ))}
            </div>
          </section>
        )}
        {/* Other uploads of this voice (utils/otherVersions.ts). */}
        {modelData?.otherVersions?.length > 0 && (
          <section className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5">
            <h2 className={h2}>{t("voice.otherVersions.title", { name })}</h2>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
              {modelData.otherVersions.map((model) => (
                <JamVoiceCard key={model._id} model={model} />
              ))}
            </div>
          </section>
        )}
        {/* Server-rendered, so the links are in the HTML crawlers fetch. */}
        {modelData?.related?.length > 0 && (
          <section className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5">
            <div className="tw-flex tw-items-end tw-justify-between tw-gap-3">
              <h2 className={h2}>{t("voice.related.title", { name })}</h2>
              {hubBadge && (
                <Link
                  href={`/ai-voices/${badgeToSlug(hubBadge)}`}
                  className="tw-whitespace-nowrap tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
                >
                  {t("voice.seeAll")}
                </Link>
              )}
            </div>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
              {modelData.related.map((model) => (
                <JamVoiceCard key={model._id} model={model} />
              ))}
            </div>
          </section>
        )}
        {collections?.data?.collections?.length > 0 && (
          <ModelCollectionsWithVoice
            collections={collections?.data?.collections}
            artistname={modelData?.artistname}
          />
        )}

        <section className="tw-flex tw-flex-col tw-gap-3">
          <h2 className={h2}>{t("voice.about.title", { name })}</h2>
          {seo?.intro ? (
            seo.intro.split(/\n\n+/).map((para, i) => (
              <p
                key={i}
                className="tw-m-0 tw-max-w-[860px] tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-muted"
              >
                {para}
              </p>
            ))
          ) : (
            <p className="tw-m-0 tw-max-w-[860px] tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
              {modelData?.description ? `${modelData.description} ` : ""}
              {t("voice.about.use", { name })}
              {modelData?.classification?.length
                ? ` ${t("voice.about.categorised", {
                    list: modelData.classification
                      .slice(0, 3)
                      .map(badgeInSentence)
                      .join(", "),
                  })}`
                : ""}
              {modelData?.uses > 0
                ? ` ${t("voice.about.uses", { n: millify(modelData.uses) })}`
                : ""}
            </p>
          )}
        </section>
        {seo?.howToSteps?.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-3.5">
            <h2 className={h2}>{t("voice.howTo.title", { name })}</h2>
            <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-2.5 tw-p-0 md:tw-grid-cols-3">
              {seo.howToSteps.map((step, i) => (
                <li
                  key={i}
                  className="tw-flex tw-gap-3 tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-4"
                >
                  <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-sm tw-font-extrabold">
                    {i + 1}
                  </span>
                  <div>
                    <div className="tw-text-[15px] tw-font-extrabold">
                      {step.name}
                    </div>
                    <p className="tw-m-0 tw-mt-0.5 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                      {step.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-6 tw-rounded-[34px] tw-bg-jam-lav tw-p-5 md:tw-grid-cols-[1fr_1.6fr] md:tw-gap-8 md:tw-rounded-[40px] md:tw-p-9">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold tw-leading-[1.05] md:tw-text-[34px]">
              {t("voice.faq.title", { name })}
            </h2>
            <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted">
              {t("voice.faq.sub", { name })}
            </p>
            <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">
              <Link href="/" className={linkPill}>
                {t("voice.link.aiCover")}
              </Link>
              <Link href="/ai-voice-changer" className={linkPill}>
                {t("voice.link.voiceChanger")}
              </Link>
              <Link href="/models" className={linkPill}>
                {t("voice.link.browseAll")}
              </Link>
              {/* The Weights voices are where "weights gg" searches land. */}
              {/weights/i.test(modelData?.urlParam || "") && (
                <Link href="/weights-gg-alternative" className={linkPill}>
                  {t("voice.link.weights")}
                </Link>
              )}
            </div>
          </div>
          <Accordion
            type="single"
            collapsible
            className="tw-flex tw-w-full tw-flex-col tw-gap-2 [&_h3]:tw-m-0"
          >
            {seo?.faq?.map((item, i) => (
              <AccordionItem
                key={`seo-${i}`}
                value={`seo-faq-${i}`}
                className={faqItem}
              >
                <AccordionTrigger className={faqTrigger}>
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className={faqContent}>
                  <div>{item.answer}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
            {!seo?.faq?.length && (
              <AccordionItem value="item-4" className={faqItem}>
                <AccordionTrigger className={faqTrigger}>
                  {t("voice.faq.how.q", { name })}
                </AccordionTrigger>
                <AccordionContent className={faqContent}>
                  <div>
                    {howAnswer[0]}
                    <Link
                      style={{
                        fontWeight: 600,
                        color: "#782dc8",
                        textDecoration: "underline",
                      }}
                      href="/dmca-policy"
                      target="_blank"
                    >
                      {t("voice.faq.dmca")}
                    </Link>
                    {howAnswer[1]}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem value="item-5" className={faqItem}>
              <AccordionTrigger className={faqTrigger}>
                {t("voice.faq.input.q", { name })}
              </AccordionTrigger>
              <AccordionContent className={faqContent}>
                <div>{t("voice.faq.input.a")}</div>
              </AccordionContent>
            </AccordionItem>

            {!seo?.faq?.length && (
              <>
                <AccordionItem value="item-6" className={faqItem}>
                  <AccordionTrigger className={faqTrigger}>
                    {t("voice.faq.time.q", { name })}
                  </AccordionTrigger>
                  <AccordionContent className={faqContent}>
                    <div>{t("voice.faq.time.a", { name })}</div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7" className={faqItem}>
                  <AccordionTrigger className={faqTrigger}>
                    {t("voice.faq.credits.q", { name })}
                  </AccordionTrigger>
                  <AccordionContent className={faqContent}>
                    <div>{t("voice.faq.credits.a", { name })}</div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className={faqItem}>
                  <AccordionTrigger className={faqTrigger}>
                    {t("voice.faq.acapella.q")}
                  </AccordionTrigger>
                  <AccordionContent className={faqContent}>
                    <div>{t("voice.faq.acapella.a", { name })}</div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9" className={faqItem}>
                  <AccordionTrigger className={faqTrigger}>
                    {t("voice.faq.changer.q", { name })}
                  </AccordionTrigger>
                  <AccordionContent className={faqContent}>
                    <div>{t("voice.faq.changer.a", { name })}</div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10" className={faqItem}>
                  <AccordionTrigger className={faqTrigger}>
                    {t("voice.faq.free.q", { name })}
                  </AccordionTrigger>
                  <AccordionContent className={faqContent}>
                    <div>{t("voice.faq.free.a", { name })}</div>
                  </AccordionContent>
                </AccordionItem>
              </>
            )}
          </Accordion>
        </section>
        {seoRelated?.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-3.5">
            <h2 className={h2}>{t("voice.pairWell.title", { name })}</h2>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {seoRelated.map((r) => (
                <Link
                  key={r.urlParam}
                  href={`/${r.urlParam}`}
                  className="tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line tw-px-4 tw-py-2 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-border-jam-purple"
                >
                  {t("voice.name", { name: r.artistname })}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default React.memo(ModelPage);

export async function getServerSideProps(context) {
  const Model = (await import("../models/Model")).default;
  const { model } = context.query;
  const { req } = context;
  const token = context?.req?.cookies?.token;

  // Cache headers for CDN. The page body includes a per-user `isLiked` flag, so
  // only anonymous requests (logged-out users and crawlers) may be served the
  // shared public cache — otherwise one user's like-state would leak to others.
  // Authenticated requests stay private. Anonymous is also the SEO-relevant path.
  if (token) {
    context.res.setHeader("Cache-Control", "private, no-store");
  } else {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=3600, stale-while-revalidate=86400",
    );
  }

  // Move database connection to only when needed
  let firebaseUid;
  if (token) {
    try {
      const decodedToken = await auth.verifyIdToken(token);
      firebaseUid = decodedToken.uid;
    } catch (error) {
      // Token is invalid, but continue without auth
    }
  }

  const getModelData = async (urlParam) => {
    // Only connect to DB when actually fetching data
    await connectToDb();

    // Optimize the aggregation pipeline
    let pipeline = [
      {
        $match: { urlParam: urlParam },
      },
      // Add early projection to reduce data transfer
      {
        $project: {
          ...MODEL_PROJECTION,
          // Server-only: decides isOwner below, then removed from the props.
          accessIds: 1,
          isLiked: {
            $in: [firebaseUid || null, { $ifNull: ["$likedBy", []] }],
          },
          baseName: 1,
          demos: 1,
          seoContent: 1,
          canonicalUrlParam: 1,
          username: "$user.username",
          avatar: "$user.avatar",
          collection: 1,
        },
      },
    ];

    // Use findOne with lean() for better performance
    const [model] = await Model.aggregate(pipeline).allowDiskUse(true).exec();
    return model;
  };

  try {
    // Early return for missing model parameter
    if (!model) {
      return notFoundPage(context);
    }

    const modelData = await getModelData(model);

    // Handle not found case
    if (!modelData) {
      return notFoundPage(context);
    }

    // Removed/deleted models are gone: a real 404, before any other branch.
    // The old "Removed" page returned 200 with the artist name, image and full
    // model record, marked index,follow - so a voice taken down on a rights
    // complaint still showed up in search looking live. Checked before the
    // private branch, which would otherwise render a removed private model.
    if (modelData?.isRemoved || modelData?.deleted) {
      return notFoundPage(context);
    }

    // Handle training models in production
    if (modelData?.training === true && process.env.NODE_ENV === "production") {
      return {
        redirect: {
          destination: localePath(context.locale, "/models"),
          permanent: false,
        },
      };
    }

    // accessIds are the owners' Firebase uids. They decide ownership here and
    // never reach the page: they used to ship in __NEXT_DATA__ on every voice
    // page, public or private.
    const accessIds = modelData.accessIds ?? [];
    const isOwner = !!firebaseUid && accessIds.includes(firebaseUid);

    // Private voices are a 404 for everyone but the owner. They used to answer
    // 200 "This voice is private." marked index,follow, with the whole record
    // (owner uid, description, demos) in the props. The status is set by hand
    // rather than notFound so the panel still renders: an owner whose token
    // cookie had expired gets it re-checked once auth loads (see the page).
    // no-store so a cached anonymous 404 is never served to that owner.
    if (modelData?.private && !isOwner) {
      context.res.statusCode = 404;
      context.res.setHeader("Cache-Control", "private, no-store");
      return {
        props: {
          isPrivate: true,
        },
      };
    }

    // Hub links and related voices (see utils/relatedVoices.ts). Both read
    // Redis-cached lists; a failure here must never fail the voice page. The
    // Redis client retries for seconds when Redis is unreachable, so the
    // whole lookup also has a time budget: past it the page renders without
    // the hub links and the related row rather than waiting.
    let hubBadgeSlugs = [];
    let relatedBadgeSlug = null;
    let related = [];
    try {
      const lookup = (async () => {
        const { getHubCategories, slugifyBadge } = await import(
          "../utils/categoryHubs"
        );
        const shown = (modelData.badges ?? []).slice(0, 3);
        // 25: below that a hub is a thin page (see pages/ai-voices/[category]).
        // Every badge the cron lists (5+ voices); hubs are the 25+ ones.
        const cats = new Map(
          (await getHubCategories(5)).map((c) => [c.slug, c]),
        );
        const isHub = (slug) => (cats.get(slug)?.count ?? 0) >= 25;
        // Related voices start from the voice's most specific badge, the one
        // with the fewest voices ("family guy", "dbz"), and are topped up from
        // the next one ("talking", "anime"). The first hub badge used to be
        // the only source, usually the broad one, so every anime voice page
        // showed the same twelve popular anime voices. Language badges
        // ("japanese") go last: they say how a voice sounds, not what it is.
        const rank = (b) => {
          const c = cats.get(slugifyBadge(b));
          return (c.group === "language" ? 1e9 : 0) + c.count;
        };
        const ordered = shown
          .filter((b) => cats.has(slugifyBadge(b)))
          .sort((a, b) => rank(a) - rank(b))
          .slice(0, 2);
        const { getRelatedVoices } = await import("../utils/relatedVoices");
        const lists = await Promise.all(
          ordered.map((b) => getRelatedVoices(b, String(modelData._id))),
        );
        const seen = new Set();
        const relatedVoices = lists
          .flat()
          .filter((v) => !seen.has(String(v._id)) && seen.add(String(v._id)))
          .slice(0, 12);
        return {
          hubBadgeSlugs: shown.map(slugifyBadge).filter(isHub),
          relatedBadgeSlug: ordered[0] ? slugifyBadge(ordered[0]) : null,
          related: relatedVoices,
        };
      })();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("timed out after 1500ms")),
          1500,
        );
      });
      try {
        ({ hubBadgeSlugs, relatedBadgeSlug, related } = await Promise.race([
          lookup,
          timeout,
        ]));
      } finally {
        clearTimeout(timer);
        lookup.catch(() => {}); // a late rejection must not go unhandled
      }
    } catch (error) {
      console.error("voice page related/hub lookup failed:", error);
    }

    // The first page of comments ("Top", as the section opens), so they are
    // in the HTML; the section refetches in the browser for like states.
    // Same rule as the lookups above: never fail or stall the voice page.
    let initialComments = null;
    try {
      const { getModelComments } = await import("../utils/modelComments");
      let timer;
      const lookup = getModelComments({
        modelId: String(modelData._id),
        limit: 5,
        sort: "top",
      });
      try {
        initialComments = await Promise.race([
          lookup,
          new Promise((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("timed out after 1000ms")),
              1000,
            );
          }),
        ]);
      } finally {
        clearTimeout(timer);
        lookup.catch(() => {});
      }
    } catch (error) {
      console.error("voice page comments lookup failed:", error);
    }

    // Same rule as the lookup above: never fail or stall the voice page.
    let otherVersions = [];
    try {
      const { getOtherVersions } = await import("../utils/otherVersions");
      let timer;
      const lookup = getOtherVersions(modelData);
      try {
        otherVersions = await Promise.race([
          lookup,
          new Promise((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("timed out after 1000ms")),
              1000,
            );
          }),
        ]);
      } finally {
        clearTimeout(timer);
        lookup.catch(() => {});
      }
    } catch (error) {
      console.error("voice page other versions lookup failed:", error);
    }

    const { isVoiceIndexable, voiceLocales } = await import(
      "../utils/voiceIndexing"
    );

    // Optimize data serialization - remove unnecessary fields
    const optimizedModelData = {
      ...modelData,
      hubBadgeSlugs,
      relatedBadgeSlug,
      related,
      otherVersions,
      initialComments,
      indexable: isVoiceIndexable(modelData),
      // Languages this page is submitted in (es/pt only for Latino/Brazilian
      // dubs and the like, see utils/voiceIndexing.ts).
      indexLocales: voiceLocales(modelData),
      // Owner uids stay on the server; the page only needs these two answers.
      accessIds: undefined,
      isOwner,
      hasCreator: accessIds.length > 0,
      // Remove large arrays if not needed on initial load
      likedBy: undefined,
      // Keep only necessary demo data
      librarySongDemos: modelData.librarySongDemos?.map((demo) => ({
        status: demo.status,
      })),
    };

    return {
      props: {
        modelData: JSON.parse(JSON.stringify(optimizedModelData)),
        token: token ?? null,
      },
    };
  } catch (error) {
    console.error("Error fetching model data:", error);

    // A database failure is a 500, never a 404: a 404 told Google the voice
    // was gone, and the anonymous s-maxage set above cached it for an hour.
    context.res.setHeader("Cache-Control", "private, no-store");
    throw error;
  }
}
